import { createFileRoute } from "@tanstack/react-router";
import { asSearchString } from "@repo/utils/search-params";
import { useState, useEffect } from "react";
import { getRouteApi } from "@tanstack/react-router";
import { Loader2, Check } from "lucide-react";
import { ToastUtil } from "@/utils/toast";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ReportAPI } from "@repo/api/report";
import { ReportStatus } from "@repo/api/report.type";
import type { ReportType } from "@repo/api/report.type";
import { SafeAreaContainer } from "@/components/layout/safe-area-container";
import { Button } from "@heroui/react";
import BaseModal from "@/components/modal/base-modal";
import { ReportTargetCard } from "./-components/ReportTargetCard";

export type ReportDetailSearch = {
  id?: string;
};

export const Route = createFileRoute("/report/detail")({
  validateSearch: (search: Record<string, unknown>): ReportDetailSearch => ({
    id: asSearchString(search.id),
  }),
  component: ReportDetailPage,
});


/**
 * 时间轴步骤组件
 */
interface TimelineStep {
    label: string;
    completed: boolean;
    active: boolean;
}

function ProgressTimeline({ status }: { status: number }) {
    // 根据状态计算当前步骤
    // 0 = 已提交, 1 = 处理中, 90/91/92 = 已结束
    const getStepIndex = (status: number): number => {
        switch (status) {
            case ReportStatus.SUBMITTED:
                return 0; // 已提交
            case ReportStatus.PROCESSING:
                return 1; // 受理中
            case ReportStatus.COMPLETED:
            case ReportStatus.REJECTED:
            case ReportStatus.CANCELLED:
                return 2; // 已结束
            default:
                return 0;
        }
    };

    const currentStep = getStepIndex(status);

    // 是否已结束（已结束时所有步骤都显示对勾）
    const isFinished = currentStep === 2;

    const steps: TimelineStep[] = [
        { label: "已提交", completed: currentStep >= 0, active: currentStep === 0 && !isFinished },
        { label: "受理中", completed: currentStep >= 1, active: currentStep === 1 && !isFinished },
        { label: "已结束", completed: currentStep >= 2, active: false },
    ];

    // 计算进度条百分比
    const progressPercent = currentStep === 0 ? 0 : currentStep === 1 ? 50 : 100;

    return (
        <div className="bg-white rounded-lg p-4 w-full">
            <div className="relative">
                {/* 进度条背景 */}
                <div className="absolute top-[11px] left-[16.67%] right-[16.67%] h-0.5 bg-gray-200 rounded-full" />

                {/* 进度条前景（动态进度） */}
                <div
                    className="absolute top-[11px] left-[16.67%] h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progressPercent * 0.6667}%` }}
                />

                {/* 步骤节点 */}
                <div className="relative flex justify-between items-start">
                    {steps.map((step, index) => (
                        <div key={index} className="flex flex-col items-center" style={{ width: '33.33%' }}>
                            {/* 圆点 */}
                            <div
                                className={`
                                    w-6 h-6 rounded-full flex items-center justify-center 
                                    transition-all duration-300 z-10
                                    ${step.completed
                                        ? step.active
                                            ? 'bg-blue-500 ring-2 ring-blue-100 shadow-md'
                                            : 'bg-blue-500'
                                        : 'bg-gray-200'
                                    }
                                `}
                            >
                                {step.completed ? (
                                    step.active ? (
                                        <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                                    ) : (
                                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                                    )
                                ) : (
                                    <div className="w-2 h-2 bg-gray-400 rounded-full" />
                                )}
                            </div>

                            {/* 标签 */}
                            <span
                                className={`
                                    mt-2 text-xs font-medium transition-colors duration-300
                                    ${step.completed
                                        ? step.active
                                            ? 'text-blue-600'
                                            : 'text-gray-700'
                                        : 'text-gray-400'
                                    }
                                `}
                            >
                                {step.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/**
 * 举报详情页面
 * 
 * URL参数：
 * - id: 举报ID
 */
export default function ReportDetailPage() {
    useDocumentTitle("举报详情 - 兽聚汇");

    const { id: reportIdParam } = getRouteApi("/report/detail").useSearch();
    const reportId = reportIdParam || "";

    const [reportDetail, setReportDetail] = useState<{
        id: string;
        target_id: string;
        type: ReportType;
        content: string;
        attachments: string[];
        status: number;
        results?: string;
        create_at: string;
    } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCancelling, setIsCancelling] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    useEffect(() => {
        if (!reportId) {
            ToastUtil.showError("缺少举报ID参数");
            return;
        }

        setIsLoading(true);
        ReportAPI.getReportDetail(reportId)
            .then((response) => {
                setReportDetail(response.data);
            })
            .catch((error) => {
                console.error("加载举报详情失败:", error);
                ToastUtil.showError("加载举报详情失败");
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [reportId]);

    // 状态文本映射
    const getStatusText = (status: number) => {
        switch (status) {
            case ReportStatus.SUBMITTED:
                return "已提交";
            case ReportStatus.PROCESSING:
                return "处理中";
            case ReportStatus.COMPLETED:
                return "已完成处理";
            case ReportStatus.REJECTED:
                return "暂不受理";
            case ReportStatus.CANCELLED:
                return "用户撤销";
            default:
                return "未知状态";
        }
    };

    // 状态颜色映射
    const getStatusColor = (status: number) => {
        switch (status) {
            case ReportStatus.SUBMITTED:
                return "text-yellow-600";
            case ReportStatus.PROCESSING:
                return "text-blue-600";
            case ReportStatus.COMPLETED:
                return "text-green-600";
            case ReportStatus.REJECTED:
                return "text-red-600";
            case ReportStatus.CANCELLED:
                return "text-gray-500";
            default:
                return "text-gray-600";
        }
    };

    // 判断是否可以撤销举报（只有已提交和处理中状态可以撤销）
    const canCancel = (status: number) => {
        return status === ReportStatus.SUBMITTED ||
            status === ReportStatus.PROCESSING;
    };

    // 打开撤销确认弹窗
    const handleOpenCancelConfirm = () => {
        setShowCancelConfirm(true);
    };

    // 确认撤销举报
    const handleConfirmCancel = () => {
        if (!reportDetail) return;

        setIsCancelling(true);
        ReportAPI.cancelReport(reportDetail.id)
            .then(() => {
                ToastUtil.showSuccess("举报已撤销");
                // 更新本地状态
                setReportDetail({
                    ...reportDetail,
                    status: ReportStatus.CANCELLED
                });
                setShowCancelConfirm(false);
            })
            .catch((error) => {
                console.error("撤销举报失败:", error);
                ToastUtil.showError(error.msg || "撤销失败，请重试");
            })
            .finally(() => {
                setIsCancelling(false);
            });
    };

    if (isLoading) {
        return (
            <SafeAreaContainer className="flex flex-col items-center justify-center p-4 bg-gray-100 min-h-screen">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                <span className="mt-2 text-gray-500">加载中...</span>
            </SafeAreaContainer>
        );
    }

    if (!reportDetail) {
        return (
            <SafeAreaContainer className="flex flex-col items-center justify-center p-4 bg-gray-100 min-h-screen">
                <span className="text-gray-500">加载失败</span>
            </SafeAreaContainer>
        );
    }

    return (
        <SafeAreaContainer className="flex flex-col gap-4 items-start justify-start p-4 relative mx-auto bg-gray-100 min-h-screen">
            {/* 进度时间轴 */}
            <ProgressTimeline status={reportDetail.status} />

            {/* 举报状态 */}
            <div className="bg-white flex flex-col gap-2 items-start justify-start p-4 rounded-lg w-full">
                <div className="flex items-center justify-between w-full">
                    <div className="font-medium text-black">举报状态</div>
                    <div className={`font-medium ${getStatusColor(reportDetail.status)}`}>
                        {getStatusText(reportDetail.status)}
                    </div>
                </div>
                <div className="text-xs text-gray-500">
                    提交时间: {new Date(reportDetail.create_at).toLocaleString("zh-CN", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                    })}
                </div>
            </div>


            {/* 被举报对象 */}
            <div className="flex flex-row items-center justify-between w-full">
                <div className="flex flex-col font-medium justify-center relative text-center text-black">
                    <p>举报内容</p>
                </div>
                <div className="flex flex-row items-center justify-end gap-2">
                    <div className="text-sm text-gray-600">回执编号</div>
                    <div className="text-sm text-gray-600">#{reportDetail.id}</div>
                </div>
            </div>
            <ReportTargetCard targetId={reportDetail.target_id} type={reportDetail.type} />

            {/* 举报内容 */}
            <div className="bg-white flex flex-col gap-2 items-start justify-start p-4 rounded-lg w-full">
                <div className="font-medium text-black">举报原因</div>
                <div className="text-sm text-gray-600 whitespace-pre-wrap">
                    {reportDetail.content}
                </div>
            </div>

            {/* 举证内容 */}
            {reportDetail.attachments && reportDetail.attachments.length > 0 && (
                <div className="bg-white flex flex-col gap-2 items-start justify-start p-4 rounded-lg w-full">
                    <div className="font-medium text-black">举证内容</div>
                    <div className="grid grid-cols-3 gap-2 w-full">
                        {reportDetail.attachments.map((url, index) => (
                            <div key={index} className="aspect-square">
                                <img
                                    src={url}
                                    alt={`举证图片${index + 1}`}
                                    className="w-full h-full object-cover rounded border"
                                    onClick={() => {
                                        // 可以添加图片预览功能
                                        window.open(url, "_blank");
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 受理结果 */}
            {reportDetail.results && (
                <div className="bg-white flex flex-col gap-2 items-start justify-start p-4 rounded-lg w-full">
                    <div className="font-medium text-black">受理结果</div>
                    <div className="text-sm text-gray-600 whitespace-pre-wrap">
                        {reportDetail.results}
                    </div>
                </div>
            )}

            {/* 提示信息（仅在没有受理结果时显示） */}
            {!reportDetail.results && (
                <div className="bg-white flex flex-col gap-2 items-start justify-start p-3 rounded-lg w-full">
                    <div className="flex flex-col font-normal justify-center leading-normal text-xs text-gray-600">
                        <p>
                            我们会认真审核每一条举报，预计在3个工作日内给您答复。如有疑问，请联系客服。
                        </p>
                    </div>
                </div>
            )}

            {/* 撤销举报按钮 */}
            {canCancel(reportDetail.status) && (
                <div className="w-full">
                    <Button
                        onPress={handleOpenCancelConfirm}
                        variant="outline"
                        className="w-full border-red-500 text-red-500"
                    >
                        撤销举报
                    </Button>
                </div>
            )}

            {/* 撤销确认弹窗 */}
            <BaseModal
                open={showCancelConfirm}
                showHeader
                title="确认撤销举报"
                description="撤销后，该举报将不再被处理。确定要撤销这条举报吗？"
                confirmLoading={isCancelling}
                onCancel={() => setShowCancelConfirm(false)}
                onClose={() => setShowCancelConfirm(false)}
                onConfirm={handleConfirmCancel}
            />
        </SafeAreaContainer>
    );
}

